import {
  addMissingFactorsAndSort,
  useCashListConfiguration,
  useCashListTransactionGroupPhoenix,
  useCashTransactionGroupAddTransaction,
  useCashTransactionGroupPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import type { GetCashListTransactionGroupParams } from '@pflegenavi/frontend/api-nursing-home';
import {
  useFeatureFlag,
  useFeatureFlagEnabled,
} from '@pflegenavi/frontend/feature-flags';
import type {
  FilterDefinition,
  RowAction,
  UrlSort,
} from '@pflegenavi/web-components';
import {
  makeConfig,
  PaginatedTable2,
  useUrlTableState,
} from '@pflegenavi/web-components';
import {
  CashTransactionGroupType,
  FeatureFlag,
  FeatureFlagStatus,
} from '@pflegenavi/shared/api';
import type { Dispatch, FC, SetStateAction } from 'react';
import React, { useMemo, useState } from 'react';
import {
  useFormatDate,
  useFormatTime,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { useTranslation } from 'react-i18next';
import { Container, Stack } from '@mui/material';
import { CashManagementHeaderBreadcrumbs } from './CashOverviewPage';
import { ChangeCashMode } from './components/form/model';
import { generatePath, nhAppMainPages } from '@pflegenavi/frontend/routing';
import { useNavigate } from 'react-router-dom';
import type { CashTransactionGroupRowModel } from './components/groupTable/model';
import {
  formatCashTransactionGroupRowModel,
  fromPhoenixListEntry,
} from './components/groupTable/model';
import { TypeColumn } from './components/groupTable/TypeColumn';
import {
  CashAmountColumn,
  CheckColumn,
  DateColumn,
  InfoColumn,
  LinkAmountColumn,
} from './components/groupTable/Columns';
import { AddCashTransactionModal } from './CashTransactionGroupDetailPage';
import { AssignToExpenseManagementModal } from './components/expenseManagement/AssignToExpenseManagementModal';
import { isAssignToExpenseEligible } from './components/expenseManagement/eligibility';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { keepPreviousData } from '@tanstack/react-query';
import type {
  TransactionGroupFilterKey,
  TransactionGroupFilters,
  TransactionGroupFiltersState,
  TransactionGroupTab,
} from './useTransactionGroupFilters';
import { useTransactionGroupFilters } from './useTransactionGroupFilters';

type ColumnNames =
  | 'type'
  | 'lastUpdate'
  | 'cashAmount'
  | 'linked'
  | 'check'
  | 'info';

type ListParams = GetCashListTransactionGroupParams['params'];
type SortColumn = NonNullable<ListParams['orderBy']>;

const SORT_COLUMNS: readonly SortColumn[] = ['update_date'];
const DEFAULT_SORT: UrlSort<SortColumn> = {
  column: 'update_date',
  direction: 'desc',
};
const DEFAULT_PAGE_SIZE = 10;

// Same type scoping as the legacy mismatchOnly / mismatchRisk flags: a
// mismatch never counts for transfers and adjustments, and deposits only
// carry a risk.
const MISMATCH_TYPES = [
  CashTransactionGroupType.ReceiptWithdrawal,
  CashTransactionGroupType.ResidentWithdrawal,
  CashTransactionGroupType.ResidentDeposit,
];
const MISMATCH_RISK_TYPES = [
  ...MISMATCH_TYPES,
  CashTransactionGroupType.Deposit,
];

type TabParams = Pick<
  ListParams,
  'groupTypes' | 'mismatch' | 'mismatchRisk' | 'excludeWithExpense'
>;

const TAB_PARAMS: Record<TransactionGroupTab, TabParams> = {
  all: { excludeWithExpense: false },
  // Groups already assigned to an expense are handled there, not a mismatch
  mismatch: {
    mismatch: true,
    groupTypes: MISMATCH_TYPES,
    excludeWithExpense: true,
  },
  mismatchRisk: {
    mismatchRisk: true,
    groupTypes: MISMATCH_RISK_TYPES,
    excludeWithExpense: true,
  },
  deposit: {
    groupTypes: [
      CashTransactionGroupType.Deposit,
      CashTransactionGroupType.ResidentDeposit,
    ],
    excludeWithExpense: false,
  },
  withdrawal: {
    groupTypes: [
      CashTransactionGroupType.ReceiptWithdrawal,
      CashTransactionGroupType.ResidentWithdrawal,
    ],
    excludeWithExpense: false,
  },
  transfer: {
    groupTypes: [CashTransactionGroupType.Transfer],
    excludeWithExpense: false,
  },
  adjustment: {
    groupTypes: [CashTransactionGroupType.Adjustment],
    excludeWithExpense: false,
  },
};

const useTabCount = (
  scope: ListParams,
  tab: TransactionGroupTab,
  enabled = true
) => {
  const { data } = useCashListTransactionGroupPhoenix(
    { ...scope, ...TAB_PARAMS[tab], page: 1, pageSize: 1 },
    { enabled, placeholderData: keepPreviousData }
  );
  return data?.meta.total_count ?? 0;
};

function useTransactionGroupFilterDefinitions(
  filtersState: TransactionGroupFiltersState
): Array<FilterDefinition<TransactionGroupFilterKey>> {
  const { t } = useTranslation();
  const { values, set, filters, cashLists } = filtersState;

  const cashListOptions = useMemo(
    () =>
      cashLists.map((cashList) => ({ id: cashList.id, label: cashList.name })),
    [cashLists]
  );

  return useMemo(
    () => [
      {
        key: 'updateDate',
        type: 'dateRange',
        label: t('cashManagement.link.table-header.lastUpdate'),
        range: values.updateDate,
        onChange: (range) => set('updateDate', range),
      },
      {
        key: 'cashList',
        type: 'select',
        label: t('cashManagement.filters.cash-list'),
        highlighted: true,
        // Always resolves to a cash list, so there is nothing to clear.
        clearable: false,
        options: cashListOptions,
        value: filters.cashListId,
        onChange: (value) => set('cashList', value),
      },
    ],
    [t, values.updateDate, filters.cashListId, set, cashListOptions]
  );
}

interface CashTransactionGroupTableProps {
  filtersState: TransactionGroupFiltersState;
  filters: TransactionGroupFilters;
  setOpen: Dispatch<
    SetStateAction<
      | undefined
      | {
          id: string;
          type: 'withdrawal' | 'deposit';
          cashListId: string;
        }
    >
  >;
}

export const CashTransactionGroupTable: FC<CashTransactionGroupTableProps> = ({
  setOpen,
  filtersState,
  filters,
}) => {
  const fDate = useFormatDate();
  const fTime = useFormatTime();
  const { fCurrencyCents } = useFormatting();
  const { t } = useTranslation();

  const hasAccountingPaymentsNew = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  );

  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );

  const mismatchRiskTabEnabled = useFeatureFlagEnabled(
    FeatureFlag.MismatchRiskTab
  );

  const { pliantReferenceCashListId } = useCashListConfiguration();

  const navigate = useNavigate();

  const { page, pageSize, sort, setPage, setPageSize, setSort } =
    useUrlTableState({
      defaultPageSize: DEFAULT_PAGE_SIZE,
      sortColumns: SORT_COLUMNS,
      defaultSort: DEFAULT_SORT,
    });
  const activeSort = sort ?? DEFAULT_SORT;

  const [expenseTarget, setExpenseTarget] =
    useState<CashTransactionGroupRowModel | null>(null);

  // The list rows carry no Pliant data, so fetch the selected group to
  // prefill the expense receipt date with the Pliant transaction date.
  const { data: expenseTargetGroup, isLoading: expenseTargetGroupLoading } =
    useCashTransactionGroupPhoenix(expenseTarget?.id);

  const scope = useMemo<ListParams>(
    () => ({
      cashListIds: filters.cashListId ? [filters.cashListId] : undefined,
      updateDateFrom: filters.updateDateFrom,
      updateDateTo: filters.updateDateTo,
      page: 1,
      pageSize: 1,
    }),
    [filters.cashListId, filters.updateDateFrom, filters.updateDateTo]
  );

  const rowCount = {
    all: useTabCount(scope, 'all'),
    deposit: useTabCount(scope, 'deposit'),
    withdrawal: useTabCount(scope, 'withdrawal'),
    transfer: useTabCount(scope, 'transfer'),
    adjustment: useTabCount(scope, 'adjustment'),
    mismatch: useTabCount(scope, 'mismatch'),
    mismatchRisk: useTabCount(scope, 'mismatchRisk', mismatchRiskTabEnabled),
  };

  const transferEnabled =
    useFeatureFlag(FeatureFlag.AccountingPayments) ===
    FeatureFlagStatus.Enabled;

  // A tab behind a disabled flag is not rendered, so a link to it shows all.
  const tab =
    (filters.tab === 'transfer' && !transferEnabled) ||
    (filters.tab === 'mismatchRisk' && !mismatchRiskTabEnabled)
      ? 'all'
      : filters.tab;

  const { data: rows, isLoading } = useCashListTransactionGroupPhoenix(
    {
      ...scope,
      ...TAB_PARAMS[tab],
      page: page + 1, // Flop is 1-indexed, MUI TablePagination is 0-indexed
      pageSize,
      orderBy: activeSort.column,
      orderDirection: activeSort.direction,
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const transformed = useMemo(() => {
    return (
      rows?.cash_transaction_group_list.map((group) =>
        formatCashTransactionGroupRowModel(fromPhoenixListEntry(group), {
          fDate,
          fTime,
          fCurrencyCents,
          t,
          useGenericTypeNames: expenseManagementEnabled,
        })
      ) ?? []
    );
  }, [rows, fDate, fTime, fCurrencyCents, t, expenseManagementEnabled]);

  const options = useMemo(() => {
    return {
      items: (row: CashTransactionGroupRowModel) => {
        const assignExpenseItems: Array<
          RowAction<CashTransactionGroupRowModel>
        > = [];
        if (expenseManagementEnabled && isAssignToExpenseEligible(row)) {
          assignExpenseItems.push({
            key: 'assign-to-expense',
            label: t('cashManagement.actions.assign-to-expense'),
            onClick: (clickedRow) => {
              setExpenseTarget(clickedRow);
            },
          });
        }

        if (
          row.cashListId === pliantReferenceCashListId ||
          row.type === CashTransactionGroupType.ResidentDeposit ||
          row.type === CashTransactionGroupType.Transfer ||
          row.type === CashTransactionGroupType.Adjustment
        ) {
          return assignExpenseItems;
        }
        return [
          ...assignExpenseItems,
          {
            label: t('cashManagement.table-row.deposit'),
            onClick: () => {
              setOpen({
                id: row.id,
                type: 'deposit',
                cashListId: row.cashListId,
              });
            },
          },
          {
            label: t('cashManagement.table-row.withdrawal'),
            onClick: () => {
              setOpen({
                id: row.id,
                type: 'withdrawal',
                cashListId: row.cashListId,
              });
            },
          },
        ];
      },
    };
  }, [setOpen, t, expenseManagementEnabled, pliantReferenceCashListId]);

  const groupDetailPath = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  )
    ? nhAppMainPages.NURSING_HOME_ACCOUNTING_GROUP_DETAIL
    : nhAppMainPages.CASH_MANAGEMENT_GROUP_DETAIL;

  const filterItems = useTransactionGroupFilterDefinitions(filtersState);
  const setTab = filtersState.set;

  const recentCashTransactionGroupsConfig = useMemo(
    () =>
      makeConfig<
        CashTransactionGroupRowModel,
        ColumnNames,
        TransactionGroupFilterKey
      >({
        showAll: undefined,
        onClickRow: (row) => {
          navigate(
            generatePath(groupDetailPath, {
              groupId: row.id,
            })
          );
        },
        tabs: {
          selectedItem: tab,
          updateFilterModel: (tab: TransactionGroupTab) =>
            setTab('tab', tab === 'all' ? undefined : tab),
          items: [
            {
              value: 'all',
              label: t('receiptType.all'),
              color: 'default',
              count: rowCount.all,
            },
            ...(mismatchRiskTabEnabled
              ? [
                  {
                    value: 'mismatchRisk',
                    label: t('cashManagement.table-tab.mismatch-risk'),
                    color: 'warning',
                    count: rowCount.mismatchRisk,
                  } as const,
                ]
              : []),
            {
              value: 'mismatch',
              label: t('cashManagement.table-tab.mismatch'),
              color: 'warning',
              count: rowCount.mismatch,
              separatorAfter: true,
            },
            {
              value: 'deposit',
              label: t('cashManagement.deposit'),
              color: 'success',
              count: rowCount.deposit,
            },
            {
              value: 'withdrawal',
              label: t('cashManagement.withdrawal'),
              color: 'error',
              count: rowCount.withdrawal,
            },
            ...(transferEnabled
              ? [
                  {
                    value: 'transfer',
                    label: t('cashManagement.link.table-row.type.transfer'),
                    color: 'default',
                    count: rowCount.transfer,
                  } as const,
                ]
              : []),
            {
              value: 'adjustment',
              label: t('cashManagement.table-row.adjustment'),
              color: 'info',
              count: rowCount.adjustment,
            },
          ],
        },
        filters: { variant: 'pills', items: filterItems },
        columns: [
          {
            key: 'type',
            label: t('cashManagement.link.table-header.type'),
            align: 'left',
            component: TypeColumn,
          },
          {
            key: 'lastUpdate',
            label: t('cashManagement.link.table-header.lastUpdate'),
            component: DateColumn,
            onSort: (direction) =>
              setSort({
                column: 'update_date',
                direction: direction === 'ASC' ? 'asc' : 'desc',
              }),
          },
          {
            key: 'cashAmount',
            label: hasAccountingPaymentsNew
              ? t('cashManagement.link.table-header.amount')
              : t('cashManagement.link.table-header.cashAmount'),
            component: CashAmountColumn,
          },
          {
            key: 'linked',
            label: t('cashManagement.link.table-header.linked'),
            component: LinkAmountColumn,
          },
          {
            key: 'check',
            label: t('cashManagement.link.table-header.check'),
            component: CheckColumn,
            align: 'center',
          },
          {
            key: 'info',
            label: t('cashManagement.link.table-header.info'),
            component: InfoColumn,
            maxWidth: 260,
          },
        ],
        sortColumn: {
          column: 'lastUpdate',
          direction: activeSort.direction === 'asc' ? 'ASC' : 'DESC',
        },
        // Rows are two lines of text next to a 40px avatar.
        emptyRowHeight: 73,
        pagination: {
          page,
          pageSize,
          pageSizeOptions: [5, 10, 25, 50],
          total: rows?.meta.total_count ?? 0,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        },
        options,
      }),
    [
      options,
      page,
      pageSize,
      setPage,
      setPageSize,
      setSort,
      activeSort.direction,
      rows?.meta.total_count,
      rowCount.adjustment,
      rowCount.transfer,
      rowCount.withdrawal,
      rowCount.deposit,
      rowCount.mismatch,
      rowCount.mismatchRisk,
      mismatchRiskTabEnabled,
      tab,
      setTab,
      filterItems,
      t,
      navigate,
      rowCount.all,
      transferEnabled,
      groupDetailPath,
      hasAccountingPaymentsNew,
    ]
  );

  return (
    <>
      <PaginatedTable2
        name="CashTransactionGroupOverview"
        config={recentCashTransactionGroupsConfig}
        rows={transformed}
        isLoading={isLoading}
      />
      {expenseTarget && !expenseTargetGroupLoading && (
        <AssignToExpenseManagementModal
          open
          onClose={() => setExpenseTarget(null)}
          cashTransactionGroupId={expenseTarget.id}
          groupAmountCents={Math.abs(expenseTarget.cashChangedCents ?? 0)}
          pliantTransactionCreatedAt={expenseTargetGroup?.pliantTransactionCreatedAt?.toISOString()}
        />
      )}
    </>
  );
};

const CashOverviewPageV3: FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<
    | undefined
    | {
        id: string;
        type: 'withdrawal' | 'deposit';
        cashListId: string;
      }
  >(undefined);

  const filtersState = useTransactionGroupFilters();
  const { selectedNursingHome } = useNursingHomeContext();

  const { mutateAsync: addTransaction } = useCashTransactionGroupAddTransaction(
    selectedNursingHome?.id
  );

  const hasAccountingPaymentsNew = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  );
  const pageTitle = hasAccountingPaymentsNew
    ? t('accounting.nav.transaction-groups')
    : t('nav.cash-management.overview');

  return (
    <Container maxWidth={'lg'}>
      <CashManagementHeaderBreadcrumbs pageTitle={pageTitle} />
      <Stack
        direction="column"
        sx={{
          gap: 3,
        }}
      >
        <CashTransactionGroupTable
          filtersState={filtersState}
          filters={filtersState.filters}
          setOpen={setOpen}
        />

        <AddCashTransactionModal
          cashListId={open?.cashListId ?? ''}
          open={Boolean(open)}
          onClose={() => setOpen(undefined)}
          type={
            open?.type === 'withdrawal'
              ? ChangeCashMode.Withdraw
              : ChangeCashMode.Deposit
          }
          onAddCashTransactionGroup={(
            type,
            coins,
            bankAccountAmount,
            notes
          ) => {
            if (!open) {
              return;
            }
            const coinsSorted =
              coins !== undefined && coins.length > 0
                ? addMissingFactorsAndSort(coins).map((coin) => coin.amount)
                : undefined;
            addTransaction({
              cashTransactionGroupId: open.id,
              type: type === ChangeCashMode.Withdraw ? 'withdrawal' : 'deposit',
              coins: coinsSorted,
              bankAccountAmount: bankAccountAmount,
              notes,
            });
          }}
        />
      </Stack>
    </Container>
  );
};

export default CashOverviewPageV3;
